import React, { useState } from 'react';
import { HelpCircle, X, Lightbulb, AlertTriangle, Target, BookOpen } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';

/**
 * Educational Tooltip Component
 * Displays rich educational content for trading terms
 * 
 * @param {Object} content - Educational content object with name, shortDesc, fullDesc, etc.
 * @param {string} variant - 'inline' (icon next to text) or 'trigger' (wraps children)
 * @param {React.ReactNode} children - Optional children to wrap
 */
const EducationalTooltip = ({ content, variant = 'inline', children, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);

  if (!content) return children || null;

  const renderTooltipContent = () => (
    <div className="w-80 max-w-sm p-0">
      {/* Header */}
      <div className="bg-primary/20 px-4 py-3 border-b border-border/50 rounded-t-md">
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-white text-sm flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-primary" />
            {content.name}
          </h4>
        </div>
        {content.shortDesc && (
          <p className="text-xs text-slate-300 mt-1">{content.shortDesc}</p>
        )}
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3">
        {/* Full Description */}
        {content.fullDesc && (
          <p className="text-xs text-slate-200 leading-relaxed">
            {content.fullDesc}
          </p>
        )}

        {/* Usage / Example */}
        {(content.usage || content.example) && (
          <div className="bg-slate-800/70 rounded-md p-2.5 border border-green-500/20">
            <div className="flex items-start gap-2">
              <Target className="h-3.5 w-3.5 text-green-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-[10px] text-green-400 font-semibold uppercase tracking-wide">
                  {content.usage ? 'Kullanım' : 'Örnek'}
                </span>
                <p className="text-xs text-green-300 font-mono mt-0.5">
                  {content.usage || content.example}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Best For */}
        {content.bestFor && (
          <div className="flex items-start gap-2">
            <Lightbulb className="h-3.5 w-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-[10px] text-yellow-400 font-semibold uppercase tracking-wide">En İyi</span>
              <p className="text-xs text-slate-300">{content.bestFor}</p>
            </div>
          </div>
        )}

        {/* Suitable For (Risk Profiles) */}
        {content.suitableFor && (
          <div className="flex items-start gap-2">
            <Lightbulb className="h-3.5 w-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <span className="text-[10px] text-yellow-400 font-semibold uppercase tracking-wide">Uygun Profil</span>
              <p className="text-xs text-slate-300">{content.suitableFor}</p>
            </div>
          </div>
        )}

        {/* Expected Return & Max Drawdown */}
        {(content.expectedReturn || content.maxDrawdown) && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            {content.expectedReturn && (
              <div className="bg-green-500/10 rounded px-2 py-1.5 border border-green-500/20">
                <span className="text-[10px] text-green-400 block">Beklenen Getiri</span>
                <span className="text-green-300 font-semibold">{content.expectedReturn}</span>
              </div>
            )}
            {content.maxDrawdown && (
              <div className="bg-red-500/10 rounded px-2 py-1.5 border border-red-500/20">
                <span className="text-[10px] text-red-400 block">Max Kayıp</span>
                <span className="text-red-300 font-semibold">{content.maxDrawdown}</span>
              </div>
            )}
          </div>
        )}

        {/* Best/Worst Market */}
        {(content.bestMarket || content.worstMarket) && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            {content.bestMarket && (
              <div className="bg-green-500/10 rounded px-2 py-1.5 border border-green-500/20">
                <span className="text-[10px] text-green-400 block">✅ En İyi Piyasa</span>
                <span className="text-slate-300 text-[11px]">{content.bestMarket}</span>
              </div>
            )}
            {content.worstMarket && (
              <div className="bg-red-500/10 rounded px-2 py-1.5 border border-red-500/20">
                <span className="text-[10px] text-red-400 block">❌ Zor Piyasa</span>
                <span className="text-slate-300 text-[11px]">{content.worstMarket}</span>
              </div>
            )}
          </div>
        )}

        {/* Common Indicators */}
        {content.commonIndicators && (
          <div className="text-xs">
            <span className="text-slate-400">Sık Kullanılan: </span>
            <span className="text-primary font-mono">{content.commonIndicators}</span>
          </div>
        )}

        {/* Trades Per Day/Week */}
        {(content.tradesPerDay || content.holdingTime) && (
          <div className="grid grid-cols-2 gap-2 text-xs">
            {content.tradesPerDay && (
              <div className="bg-blue-500/10 rounded px-2 py-1.5 border border-blue-500/20">
                <span className="text-[10px] text-blue-400 block">İşlem Sayısı</span>
                <span className="text-slate-300">{content.tradesPerDay}</span>
              </div>
            )}
            {content.holdingTime && (
              <div className="bg-blue-500/10 rounded px-2 py-1.5 border border-blue-500/20">
                <span className="text-[10px] text-blue-400 block">Tutma Süresi</span>
                <span className="text-slate-300">{content.holdingTime}</span>
              </div>
            )}
          </div>
        )}

        {/* Requirements */}
        {content.requirements && (
          <div className="text-xs text-slate-400">
            <span className="text-slate-500">Gereksinimler: </span>
            {content.requirements}
          </div>
        )}

        {/* Tip */}
        {content.tip && (
          <div className="bg-primary/10 rounded-md p-2.5 border border-primary/20">
            <div className="flex items-start gap-2">
              <Lightbulb className="h-3.5 w-3.5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-[10px] text-primary font-semibold uppercase tracking-wide">İpucu</span>
                <p className="text-xs text-slate-300">{content.tip}</p>
              </div>
            </div>
          </div>
        )}

        {/* Warning */}
        {content.warning && (
          <div className="bg-yellow-500/10 rounded-md p-2.5 border border-yellow-500/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-3.5 w-3.5 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-[10px] text-yellow-400 font-semibold uppercase tracking-wide">Dikkat</span>
                <p className="text-xs text-yellow-200">{content.warning}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip open={isOpen} onOpenChange={setIsOpen}>
        <TooltipTrigger asChild>
          {variant === 'inline' ? (
            <button
              type="button"
              className={`inline-flex items-center justify-center ml-3 px-2 py-1 bg-slate-700/80 hover:bg-primary/30 text-slate-300 hover:text-white border border-slate-600 hover:border-primary transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/50 rounded-md text-xs font-medium gap-1 ${className}`}
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(!isOpen);
              }}
            >
              <HelpCircle className="h-3.5 w-3.5" />
              <span>?</span>
            </button>
          ) : (
            <span className={`cursor-help ${className}`}>{children}</span>
          )}
        </TooltipTrigger>
        <TooltipContent
          side="top"
          align="center"
          className="bg-slate-900 border border-primary/30 p-0 shadow-xl shadow-primary/10"
          sideOffset={8}
        >
          {renderTooltipContent()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default EducationalTooltip;
